import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { getApiBaseUrl } from '../lib/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [code, setCode] = useState('');
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is already logged in, redirect to dashboard
        navigate('/dashboard', { replace: true });
      } else {
        setIsCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Show loading state while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-8 w-8 text-primary mx-auto"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Store email in localStorage for verification
      localStorage.setItem('emailForSignIn', email);

      const response = await fetch(`${getApiBaseUrl()}/auth/magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send magic link');
      }

      setIsSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setIsVerifyingCode(true);
    setCodeError(null);

    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch(`${getApiBaseUrl()}/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim(), timezone: browserTimezone }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Invalid code');
      }

      await signInWithCustomToken(auth, data.customToken);
      localStorage.removeItem('emailForSignIn');
      navigate(data.redirectTo || '/dashboard');
    } catch (err) {
      setCodeError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  if (isSent) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <span className="text-2xl sm:text-3xl">📧</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-display font-bold text-dark mb-3 sm:mb-4">
              Check your email
            </h1>
            <p className="text-dark-500 font-body mb-4 sm:mb-6 text-sm sm:text-base">
              We sent a magic link to{' '}
              <span className="font-medium text-dark break-all">{email}</span>.
              Click the link in the email to sign in.
            </p>

            <div className="border-t border-dark-100 pt-4 sm:pt-6 mt-2">
              <p className="text-sm text-dark-500 font-body mb-3">
                Or enter the code from the email:
              </p>
              <form onSubmit={handleCodeSubmit}>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="XXXXXX"
                  maxLength={6}
                  disabled={isVerifyingCode}
                  aria-label="Sign-in code"
                  className="w-full px-4 py-3 rounded-lg border border-dark-200 focus:border-primary focus:ring-2 focus:ring-primary-100 outline-none transition-colors text-center text-2xl font-mono tracking-[0.3em] uppercase disabled:opacity-50 disabled:bg-dark-50"
                />
                {codeError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{codeError}</p>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isVerifyingCode || code.length < 6}
                  className="w-full mt-3 bg-primary text-white py-3 px-4 rounded-lg font-medium active:bg-primary-800 sm:hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] touch-manipulation"
                >
                  {isVerifyingCode ? 'Verifying...' : 'Sign in with code'}
                </button>
              </form>
            </div>

            <p className="text-sm text-dark-400 font-body mt-4">
              Didn&apos;t receive it?{' '}
              <button
                onClick={() => setIsSent(false)}
                className="text-primary active:underline sm:hover:underline min-h-[44px] inline-flex items-center touch-manipulation"
              >
                Try again
              </button>
            </p>
          </div>
          <Link
            to="/"
            className="block text-center text-dark-500 mt-6 active:text-primary sm:hover:text-primary transition-colors font-body min-h-[44px] flex items-center justify-center touch-manipulation"
          >
            &larr; Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <Link
              to="/"
              className="inline-block min-h-[44px] flex items-center justify-center touch-manipulation"
            >
              <h1 className="text-xl sm:text-2xl font-display font-bold text-primary">
                Still Love Film
              </h1>
            </Link>
            <p className="text-dark-500 mt-2 font-body text-sm sm:text-base">
              Sign in to get your weekly film picks
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4 sm:mb-6">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-dark mb-2"
              >
                Email address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                disabled={isLoading}
                className="w-full px-4 py-3 rounded-lg border border-dark-200 focus:border-primary focus:ring-2 focus:ring-primary-100 outline-none transition-colors disabled:opacity-50 disabled:bg-dark-50 text-base"
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !email}
              className="w-full bg-primary text-white py-3 px-4 rounded-lg font-medium active:bg-primary-800 sm:hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] touch-manipulation"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Sending magic link...
                </span>
              ) : (
                'Send magic link'
              )}
            </button>
          </form>

          <p className="text-center text-xs sm:text-sm text-dark-400 mt-4 sm:mt-6 font-body">
            No password needed. We&apos;ll send you a magic link to sign in.
          </p>
        </div>

        <Link
          to="/"
          className="block text-center text-dark-500 mt-6 active:text-primary sm:hover:text-primary transition-colors font-body min-h-[44px] flex items-center justify-center touch-manipulation"
        >
          &larr; Back to home
        </Link>
      </div>
    </div>
  );
}
